
import { ChatMessage } from "@/lib/types";
import { Avatar } from "@/components/User/Avatar";
import { formatDistanceToNow } from "date-fns";
import { Pin, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

type MessageItemProps = {
  message: ChatMessage;
};

export const MessageItem = ({ message }: MessageItemProps) => {
  const formatTime = (timestamp: string) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  // Check if there's a valid user for this message
  if (!message.user) {
    return (
      <div className="flex items-start space-x-3">
        <div className="flex-1">
          <div className="flex items-center">
            <span className="font-medium">Unknown User</span>
            <span className="text-xs text-muted-foreground ml-2">
              {formatTime(message.createdAt)}
            </span>
          </div>
          <p className="text-muted-foreground italic">
            {message.isDeleted ? "This message has been deleted" : message.content}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-start space-x-3 ${message.isPinned ? 'border-l-4 border-yellow-400 pl-2' : ''}`}
    >
      <Link to={`/users/${message.user.id}`}>
        <Avatar 
          userId={message.user.id} 
          avatarId={message.user.avatarId} 
          size="sm" 
        />
      </Link>
      <div className="flex-1">
        <div className="flex items-center">
          <Link 
            to={`/users/${message.user.id}`}
            className="font-medium hover:underline"
          >
            {message.user.username || "Unknown User"}
          </Link>
          <span className="text-xs text-muted-foreground ml-2">
            {formatTime(message.createdAt)}
          </span>
          {message.isPinned && (
            <Pin className="h-3 w-3 text-yellow-500 ml-2" />
          )}
        </div>
        
        {/* Reply reference */}
        {message.replyToMessage && message.replyToMessage.user && (
          <div className="text-xs text-muted-foreground border-l-2 border-muted pl-2 my-1">
            <Link 
              to={`/users/${message.replyToMessage.user.id}`}
              className="font-medium hover:underline"
            >
              {message.replyToMessage.user.username || "Unknown"}
            </Link>: {message.replyToMessage.content}
          </div>
        )}
        
        {message.isDeleted ? (
          <p className="text-muted-foreground italic">This message has been deleted</p>
        ) : (
          <p className="mt-1">{message.content}</p>
        )}
        
        {/* Reactions */}
        {message.reactions && message.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Array.from(new Set(message.reactions.map(r => r.emoji))).map(emoji => {
              const count = message.reactions?.filter(r => r.emoji === emoji).length || 0;
              return (
                <div key={emoji} className="bg-muted rounded-full px-2 py-0.5 text-xs flex items-center">
                  <span>{emoji}</span>
                  <span className="ml-1">{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
